import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Eye } from "lucide-react";
import Card from "@/components/common/card/Card";
import AlertMessage from "@/components/layout/message/AlertMessage";
import { useStudentListSection } from "./useStudentListSection";
import PaginationControls from "@/components/layout/paginationControls/PaginationControls";
import ActionPanel from "@/components/layout/actionPanel/ActionPanel";
import type { user } from "@/types/api/user.api";
import type { studentListSectionProps } from "./types.props";
import StudentTable from "../studentTable/StudentTable";

const PAGE_SIZE = 15;

const StudentListSection: React.FC<studentListSectionProps> = ({
    institutionId,
    selectedSimId,
}) => {
    const navigate = useNavigate();

    const {
        students,
        count,
        currentPage,
        isLoading,
        error,
        searchTerm,
        setSearchTerm,
        setPage,
    } = useStudentListSection(institutionId);

    const studentTableActions = useMemo(
        () => [
            {
                label: "Ver resultados",
                handler: (student: user) => {
                    if (selectedSimId) {
                        navigate(
                            `/student-dashboard?simulationId=${selectedSimId}&studentId=${student.id}`,
                            { state: { from: location.pathname + location.search } },
                        );
                    } else {
                        alert("Por favor, selecciona una simulación primero.");
                    }
                },
                variant: "primary" as const,
                icon: <Eye size={16} />,
            },
        ],
        [navigate, selectedSimId, location.pathname, location.search],
    );

    return (
        <section id="institution-students">
            <Card>
                <ActionPanel
                    placeholder="Buscar por nombre, correo o identificador..."
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                />

                <AlertMessage type="error" title="Error al cargar estudiantes">
                    {error?.generalMessage}
                </AlertMessage>

                <StudentTable
                    data={students}
                    isLoading={isLoading}
                    actions={studentTableActions}
                />

                {count > PAGE_SIZE && (
                    <PaginationControls
                        currentPage={currentPage}
                        totalCount={count}
                        pageSize={PAGE_SIZE}
                        onPageChange={setPage}
                        className="mt-4"
                    />
                )}
            </Card>
        </section>
    );
};

export default StudentListSection;
